import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { mockAIProviders } from '../mock';
import { Save, Eye, EyeOff, GripVertical, Plus, Trash2, Check, X, HelpCircle, ExternalLink, BookOpen } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const apiKeyGuides = [
  {
    provider: 'OpenAI',
    icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/openai/openai-original.svg',
    description: 'Get access to GPT-4, GPT-3.5, DALL-E and more',
    steps: [
      'Go to https://platform.openai.com/api-keys',
      'Sign in or create an OpenAI account',
      'Click "Create new secret key"',
      'Copy the key (starts with sk-...)',
      'Paste it in the API Key field above'
    ],
    pricing: 'Pay-as-you-go starting at $0.0005/1K tokens',
    link: 'https://platform.openai.com/api-keys'
  },
  {
    provider: 'Anthropic',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/7/78/Anthropic_logo.svg',
    description: 'Access Claude 3 models for advanced reasoning',
    steps: [
      'Visit https://console.anthropic.com/account/keys',
      'Sign up for an Anthropic account',
      'Click "Create Key"',
      'Name your key and copy it',
      'Add it to your API settings'
    ],
    pricing: 'From $0.00025/1K tokens (Haiku)',
    link: 'https://console.anthropic.com/account/keys'
  },
  {
    provider: 'Google AI',
    icon: 'https://www.gstatic.com/lamda/images/gemini_sparkle_v002_d4735304ff6292a690345.svg',
    description: 'Use Gemini Pro and other Google AI models',
    steps: [
      'Go to https://makersuite.google.com/app/apikey',
      'Sign in with your Google account',
      'Click "Create API Key"',
      'Copy the generated key',
      'Enter it in the API settings'
    ],
    pricing: 'Free tier available, then pay-as-you-go',
    link: 'https://makersuite.google.com/app/apikey'
  }
];

const SortableProviderCard = ({ provider, onToggle, onApiKeyChange, onDelete, showApiKey, onToggleShowApiKey }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: provider.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style}
      className="bg-gray-800/50 border-gray-700 p-6 hover:bg-gray-800/70 transition-all duration-300"
    >
      <div className="flex items-start gap-4">
        {/* Drag Handle */}
        <div 
          {...attributes} 
          {...listeners}
          className="cursor-grab active:cursor-grabbing pt-2 hover:text-blue-400 transition-colors"
        >
          <GripVertical className="w-5 h-5 text-gray-500" />
        </div>

        {/* Provider Logo */}
        <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
          {provider.logo ? (
            <img src={provider.logo} alt={provider.name} className="w-8 h-8" />
          ) : (
            <span className="text-xl font-bold text-gray-400">{provider.name[0]}</span>
          )}
        </div>

        {/* Provider Info */}
        <div className="flex-1 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">{provider.name}</h3>
              <p className="text-sm text-gray-400">{provider.provider}</p>
              <p className="text-xs text-gray-500 mt-1">{provider.description}</p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={provider.enabled} 
                  onCheckedChange={() => onToggle(provider.id)}
                  className="data-[state=checked]:bg-blue-600"
                />
                <Label className="text-sm text-gray-400">Enabled</Label>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(provider.id)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* API Key Input */}
          <div className="space-y-2">
            <Label className="text-sm text-gray-400">API Key</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showApiKey[provider.id] ? 'text' : 'password'}
                  placeholder="Enter API key..."
                  value={provider.apiKey || ''}
                  onChange={(e) => onApiKeyChange(provider.id, e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white pr-10"
                />
                <button
                  onClick={() => onToggleShowApiKey(provider.id)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                >
                  {showApiKey[provider.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {provider.apiKey && (
                <div className="flex items-center px-3 bg-green-500/20 rounded-lg">
                  <Check className="w-4 h-4 text-green-400" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const APISettings = () => {
  const [providers, setProviders] = useState(mockAIProviders);
  const [showApiKey, setShowApiKey] = useState({});
  const [customProvider, setCustomProvider] = useState({ name: '', provider: '', apiKey: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setProviders((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      
      toast({
        title: 'Order updated',
        description: 'AI model order has been saved.',
      });
    }
  };

  const handleToggle = (id) => {
    setProviders(prev => 
      prev.map(p => p.id === id ? { ...p, enabled: !p.enabled } : p)
    );
  };

  const handleApiKeyChange = (id, apiKey) => {
    setProviders(prev => 
      prev.map(p => p.id === id ? { ...p, apiKey, apiKeyConfigured: !!apiKey } : p)
    );
  };

  const handleToggleShowApiKey = (id) => {
    setShowApiKey(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDelete = (id) => {
    setProviders(prev => prev.filter(p => p.id !== id));
    toast({
      title: 'Provider removed',
      description: 'AI provider has been removed.',
    });
  };

  const handleAddCustomProvider = () => {
    if (!customProvider.name || !customProvider.provider) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive'
      });
      return;
    }

    const newProvider = {
      id: customProvider.name.toLowerCase().replace(/\s+/g, '-'),
      name: customProvider.name,
      provider: customProvider.provider,
      apiKey: customProvider.apiKey,
      enabled: true,
      apiKeyConfigured: !!customProvider.apiKey,
      description: 'Custom AI provider'
    };

    setProviders(prev => [...prev, newProvider]);
    setCustomProvider({ name: '', provider: '', apiKey: '' });
    setShowAddForm(false);
    
    toast({
      title: 'Provider added',
      description: `${customProvider.name} has been added successfully.`,
    });
  };

  const handleSaveAll = () => {
    toast({
      title: 'Settings saved',
      description: 'All API configurations have been saved.',
    });
  };

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">API Settings</h1>
          <p className="text-gray-400">Configure your AI model providers and API keys</p>
        </div>

        <Tabs defaultValue="providers" className="space-y-6">
          <TabsList className="bg-gray-800/50 border border-gray-700">
            <TabsTrigger value="providers">My Providers</TabsTrigger>
            <TabsTrigger value="help">
              <HelpCircle className="w-4 h-4 mr-2" />
              How to Get API Keys
            </TabsTrigger>
          </TabsList>

          {/* Providers Tab */}
          <TabsContent value="providers" className="space-y-6">
            {/* Add Custom Provider Button */}
            <div>
              {!showAddForm ? (
                <Button 
                  onClick={() => setShowAddForm(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Custom Provider
                </Button>
              ) : (
                <Card className="bg-gray-800/50 border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Add Custom AI Provider</h3>
                  <div className="grid gap-4">
                    <div>
                      <Label className="text-sm text-gray-400 mb-2 block">Model Name</Label>
                      <Input
                        placeholder="e.g., GPT-5"
                        value={customProvider.name}
                        onChange={(e) => setCustomProvider(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-400 mb-2 block">Provider Name</Label>
                      <Input
                        placeholder="e.g., OpenAI"
                        value={customProvider.provider}
                        onChange={(e) => setCustomProvider(prev => ({ ...prev, provider: e.target.value }))}
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-400 mb-2 block">API Key (Optional)</Label>
                      <Input
                        type="password"
                        placeholder="Enter API key..."
                        value={customProvider.apiKey}
                        onChange={(e) => setCustomProvider(prev => ({ ...prev, apiKey: e.target.value }))}
                        className="bg-gray-900 border-gray-700 text-white"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleAddCustomProvider} className="bg-green-600 hover:bg-green-700">
                        <Check className="w-4 h-4 mr-2" />
                        Add Provider
                      </Button>
                      <Button 
                        onClick={() => {
                          setShowAddForm(false);
                          setCustomProvider({ name: '', provider: '', apiKey: '' });
                        }}
                        variant="outline"
                        className="border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Sortable Provider List */}
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={providers.map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-4 mb-8">
                  {providers.map((provider) => (
                    <SortableProviderCard
                      key={provider.id}
                      provider={provider}
                      onToggle={handleToggle}
                      onApiKeyChange={handleApiKeyChange}
                      onDelete={handleDelete}
                      showApiKey={showApiKey}
                      onToggleShowApiKey={handleToggleShowApiKey}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveAll}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 px-8"
              >
                <Save className="w-5 h-5 mr-2" />
                Save All Settings
              </Button>
            </div>
          </TabsContent>

          {/* Help Tab */}
          <TabsContent value="help" className="space-y-6">
            <Card className="bg-gray-800/50 border-gray-700 p-6">
              <div className="flex items-start gap-3 mb-6">
                <BookOpen className="w-6 h-6 text-blue-400 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">What is an API Key?</h2>
                  <p className="text-gray-300 leading-relaxed">
                    An API key is like a password that allows globalnow.biz to access AI services on your behalf. 
                    Each AI provider (OpenAI, Anthropic, Google) gives you a unique key when you create an account with them. 
                    You only pay the AI provider for what you use - no extra charges from us!
                  </p>
                </div>
              </div>
            </Card>

            {apiKeyGuides.map((guide, idx) => (
              <Card key={idx} className="bg-gray-800/50 border-gray-700 p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img src={guide.icon} alt={guide.provider} className="w-12 h-12" />
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-1">{guide.provider}</h3>
                    <p className="text-gray-400 text-sm">{guide.description}</p>
                    <p className="text-green-400 text-sm mt-1">💰 {guide.pricing}</p>
                  </div>
                  <a 
                    href={guide.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>

                <div className="space-y-2 mt-4">
                  <h4 className="text-sm font-semibold text-gray-300 mb-3">Step-by-step:</h4>
                  {guide.steps.map((step, stepIdx) => (
                    <div key={stepIdx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-blue-400 text-xs font-bold">{stepIdx + 1}</span>
                      </div>
                      <p className="text-gray-300 text-sm">{step}</p>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => window.open(guide.link, '_blank')}
                  variant="outline"
                  className="w-full mt-4 border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  Get {guide.provider} API Key <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default APISettings;
